const roleMapping = {
  0: 'agent',
  1: 'administrator',
};

const availabilityMapping = {
  0: 'online',
  1: 'offline',
  2: 'busy',
};

const translationKeys = {
  'automationrule:create': `AUDIT_LOGS.AUTOMATION_RULE.ADD`,
  'automationrule:update': `AUDIT_LOGS.AUTOMATION_RULE.EDIT`,
  'automationrule:destroy': `AUDIT_LOGS.AUTOMATION_RULE.DELETE`,
  'webhook:create': `AUDIT_LOGS.WEBHOOK.ADD`,
  'webhook:update': `AUDIT_LOGS.WEBHOOK.EDIT`,
  'webhook:destroy': `AUDIT_LOGS.WEBHOOK.DELETE`,
  'inbox:create': `AUDIT_LOGS.INBOX.ADD`,
  'inbox:update': `AUDIT_LOGS.INBOX.EDIT`,
  'inbox:destroy': `AUDIT_LOGS.INBOX.DELETE`,
  'user:sign_in': `AUDIT_LOGS.USER_ACTION.SIGN_IN`,
  'user:sign_out': `AUDIT_LOGS.USER_ACTION.SIGN_OUT`,
  // Impersonação pelo /super_admin: o autor é o super admin (não é agente da
  // conta, então o nome sai de audited_changes, não da lista de agentes).
  'user:impersonation_issued': `AUDIT_LOGS.USER_ACTION.IMPERSONATION_ISSUED`,
  'user:impersonation_sign_in': `AUDIT_LOGS.USER_ACTION.IMPERSONATION_SIGN_IN`,
  'team:create': `AUDIT_LOGS.TEAM.ADD`,
  'team:update': `AUDIT_LOGS.TEAM.EDIT`,
  'team:destroy': `AUDIT_LOGS.TEAM.DELETE`,
  'macro:create': `AUDIT_LOGS.MACRO.ADD`,
  'macro:update': `AUDIT_LOGS.MACRO.EDIT`,
  'macro:destroy': `AUDIT_LOGS.MACRO.DELETE`,
  'accountuser:create': `AUDIT_LOGS.ACCOUNT_USER.ADD`,
  'accountuser:update:self': `AUDIT_LOGS.ACCOUNT_USER.EDIT.SELF`,
  'accountuser:update:other': `AUDIT_LOGS.ACCOUNT_USER.EDIT.OTHER`,
  'accountuser:update:deleted': `AUDIT_LOGS.ACCOUNT_USER.EDIT.DELETED`,
  'inboxmember:create': `AUDIT_LOGS.INBOX_MEMBER.ADD`,
  'inboxmember:destroy': `AUDIT_LOGS.INBOX_MEMBER.REMOVE`,
  'teammember:create': `AUDIT_LOGS.TEAM_MEMBER.ADD`,
  'teammember:destroy': `AUDIT_LOGS.TEAM_MEMBER.REMOVE`,
  'account:update': `AUDIT_LOGS.ACCOUNT.EDIT`,
  'conversation:destroy': `AUDIT_LOGS.CONVERSATION.DELETE`,
  // Assistente Vision. O auditable é a CONVERSA; quando ela não existe mais (ou
  // a pergunta veio sem conversa) o backend grava a conta como auditable — daí
  // o par account:*, com a mesma frase.
  'conversation:vision_query': `AUDIT_LOGS.VISION.QUERY`,
  'conversation:vision_tool_call': `AUDIT_LOGS.VISION.TOOL_CALL`,
  'account:vision_query': `AUDIT_LOGS.VISION.QUERY`,
  'account:vision_tool_call': `AUDIT_LOGS.VISION.TOOL_CALL`,
  // Kanban: só configuração e destrutivo (mover/editar card fica na timeline
  // do card). O nome sai de audited_changes (handleKanban).
  'pipeline:create': `AUDIT_LOGS.KANBAN.PIPELINE.ADD`,
  'pipeline:update': `AUDIT_LOGS.KANBAN.PIPELINE.EDIT`,
  'pipeline:destroy': `AUDIT_LOGS.KANBAN.PIPELINE.DELETE`,
  'pipelinestage:create': `AUDIT_LOGS.KANBAN.STAGE.ADD`,
  'pipelinestage:update': `AUDIT_LOGS.KANBAN.STAGE.EDIT`,
  'pipelinestage:destroy': `AUDIT_LOGS.KANBAN.STAGE.DELETE`,
  'kanbanstageautomation:create': `AUDIT_LOGS.KANBAN.AUTOMATION.ADD`,
  'kanbanstageautomation:update': `AUDIT_LOGS.KANBAN.AUTOMATION.EDIT`,
  'kanbanstageautomation:destroy': `AUDIT_LOGS.KANBAN.AUTOMATION.DELETE`,
  'pipelinehandoffrule:create': `AUDIT_LOGS.KANBAN.HANDOFF.ADD`,
  'pipelinehandoffrule:update': `AUDIT_LOGS.KANBAN.HANDOFF.EDIT`,
  'pipelinehandoffrule:destroy': `AUDIT_LOGS.KANBAN.HANDOFF.DELETE`,
  'deal:destroy': `AUDIT_LOGS.KANBAN.DEAL.DELETE`,
  'kanbanstagetimer:cancel': `AUDIT_LOGS.KANBAN.SCHEDULED_ACTION.CANCEL`,
  'ai::reengagementtracker:cancel': `AUDIT_LOGS.KANBAN.SCHEDULED_ACTION.CANCEL`,
};

// Linha da auditoria é de LEITURA: pergunta longa ou args gigantes viram
// parede de texto. Trunca com reticências — o dado inteiro segue no registro.
const QUERY_MAX_LENGTH = 120;
const ARGS_MAX_LENGTH = 200;

function truncate(value, maxLength) {
  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

const KANBAN_TYPES = [
  'pipeline',
  'pipelinestage',
  'kanbanstageautomation',
  'pipelinehandoffrule',
  'deal',
  'kanbanstagetimer',
  'ai::reengagementtracker',
];

// Nome do funil/fase/automação/card na frase. No create e destroy o audited
// guarda o valor cru; no update guarda [antes, depois]. Sem nome, a frase fica
// só com o id — nunca "undefined".
function handleKanban(auditLogItem, translationPayload) {
  const changes = auditLogItem.audited_changes || {};
  const raw = changes.name ?? changes.title ?? changes.target ?? null;
  const name = Array.isArray(raw) ? raw[1] : raw;
  return {
    ...translationPayload,
    name: name ? truncate(name, 80) : `#${auditLogItem.auditable_id}`,
  };
}

function extractAttrChange(attrChange) {
  if (Array.isArray(attrChange)) {
    return attrChange[attrChange.length - 1];
  }
  return attrChange;
}

// Papel personalizado: o audit guarda só o id. Papel removido da conta vira
// "#id"; tirar o papel (volta pro padrão) vira "none" — nunca some da frase,
// é justamente a troca que precisa ficar visível.
function customRoleLabel(customRoleId, customRoles) {
  if (customRoleId === null) return 'none';

  const customRole = customRoles.find(role => role.id === customRoleId);
  return customRole ? customRole.name : `#${customRoleId}`;
}

export function extractChangedAccountUserValues(auditedChanges, customRoles) {
  let changes = [];
  let values = [];

  // Check roles
  if (auditedChanges.role && auditedChanges.role.length) {
    changes.push('role');
    values.push(roleMapping[extractAttrChange(auditedChanges.role)]);
  }

  // Check availability
  if (auditedChanges.availability && auditedChanges.availability.length) {
    changes.push('availability');
    values.push(
      availabilityMapping[extractAttrChange(auditedChanges.availability)]
    );
  }

  if (auditedChanges.custom_role_id && auditedChanges.custom_role_id.length) {
    changes.push('custom_role');
    values.push(
      customRoleLabel(
        extractAttrChange(auditedChanges.custom_role_id),
        customRoles
      )
    );
  }

  return { changes, values };
}

// vision_query:     { query, display_id, session_id, turn_id }
// vision_tool_call:  { tool, label, args, display_id, session_id, turn_id }
// Todo placeholder da frase é preenchido aqui — vue-i18n resolve o que falta
// como string vazia, e uma frase pela metade esconde o que foi auditado.
function handleVision(auditLogItem, translationPayload, action) {
  const changes = auditLogItem.audited_changes || {};

  // Sem conversa (apagada, ou pergunta fora de conversa) o backend audita a
  // conta: a frase diz "—" em vez de inventar um número.
  translationPayload.displayId = changes.display_id ?? '—';

  if (action === 'vision_query') {
    translationPayload.query = truncate(changes.query ?? '', QUERY_MAX_LENGTH);
    return translationPayload;
  }

  // `label` é o nome amigável da tool no catálogo; `tool` é o nome técnico.
  translationPayload.tool = changes.label || changes.tool || '';
  translationPayload.toolName = changes.tool || '';
  translationPayload.args = truncate(
    JSON.stringify(changes.args ?? {}),
    ARGS_MAX_LENGTH
  );
  return translationPayload;
}

function getAgentName(userId, agentList) {
  if (userId === null) {
    return 'System';
  }

  const agentName = agentList.find(agent => agent.id === userId)?.name;

  // If agent does not exist(removed/deleted), return userId
  return agentName || userId;
}

const IMPERSONATION_ACTIONS = ['impersonation_issued', 'impersonation_sign_in'];

// Toda frase leva quem entrou (e-mail do super admin) e como quem — sem isso o
// registro existe e não diz nada.
function handleImpersonation(auditLogItem, translationPayload, agentList) {
  const changes = auditLogItem.audited_changes || {};
  return {
    ...translationPayload,
    superAdmin: changes.super_admin_email || `#${auditLogItem.user_id}`,
    user:
      changes.user_email || getAgentName(auditLogItem.auditable_id, agentList),
  };
}

function handleAccountUserCreate(auditLogItem, translationPayload, agentList) {
  translationPayload.invitee = getAgentName(
    auditLogItem.audited_changes.user_id,
    agentList
  );

  const roleKey = auditLogItem.audited_changes.role;
  translationPayload.role = roleMapping[roleKey] || 'unknown'; // 'unknown' as a fallback in case an unrecognized key is provided

  return translationPayload;
}

function handleAccountUserUpdate(
  auditLogItem,
  translationPayload,
  agentList,
  customRoles
) {
  if (auditLogItem.user_id !== auditLogItem.auditable?.user_id) {
    translationPayload.user = getAgentName(
      auditLogItem.auditable?.user_id,
      agentList
    );
  }

  const accountUserChanges = extractChangedAccountUserValues(
    auditLogItem.audited_changes,
    customRoles
  );
  if (accountUserChanges) {
    translationPayload.attributes = accountUserChanges.changes;
    translationPayload.values = accountUserChanges.values;
  }
  return translationPayload;
}

function setUserInPayload(auditLogItem, translationPayload, agentList) {
  const userIdChange = auditLogItem.audited_changes.user_id;
  if (userIdChange && userIdChange !== undefined) {
    translationPayload.user = getAgentName(userIdChange, agentList);
  }
  return translationPayload;
}

function setTeamIdInPayload(auditLogItem, translationPayload) {
  if (auditLogItem.audited_changes.team_id) {
    translationPayload.team_id = auditLogItem.audited_changes.team_id;
  }
  return translationPayload;
}

function setInboxIdInPayload(auditLogItem, translationPayload) {
  if (auditLogItem.audited_changes.inbox_id) {
    translationPayload.inbox_id = auditLogItem.audited_changes.inbox_id;
  }
  return translationPayload;
}

function handleInboxTeamMember(auditLogItem, translationPayload, agentList) {
  if (auditLogItem.audited_changes) {
    translationPayload = setUserInPayload(
      auditLogItem,
      translationPayload,
      agentList
    );
    translationPayload = setTeamIdInPayload(auditLogItem, translationPayload);
    translationPayload = setInboxIdInPayload(auditLogItem, translationPayload);
  }
  return translationPayload;
}

function handleAccountUser(
  auditLogItem,
  translationPayload,
  agentList,
  customRoles,
  action
) {
  if (action === 'create') {
    return handleAccountUserCreate(auditLogItem, translationPayload, agentList);
  }

  if (action === 'update') {
    return handleAccountUserUpdate(
      auditLogItem,
      translationPayload,
      agentList,
      customRoles
    );
  }

  return translationPayload;
}

export function generateTranslationPayload(
  auditLogItem,
  agentList,
  customRoles
) {
  let translationPayload = {
    agentName: getAgentName(auditLogItem.user_id, agentList),
    id: auditLogItem.auditable_id,
  };

  const auditableType = auditLogItem.auditable_type.toLowerCase();
  const action = auditLogItem.action.toLowerCase();

  if (auditableType === 'conversation' && action === 'destroy') {
    translationPayload.id =
      auditLogItem.audited_changes?.display_id || auditLogItem.auditable_id;
  }

  if (auditableType === 'accountuser') {
    translationPayload = handleAccountUser(
      auditLogItem,
      translationPayload,
      agentList,
      customRoles,
      action
    );
  }

  if (action === 'vision_query' || action === 'vision_tool_call') {
    translationPayload = handleVision(auditLogItem, translationPayload, action);
  }

  if (auditableType === 'user' && IMPERSONATION_ACTIONS.includes(action)) {
    translationPayload = handleImpersonation(
      auditLogItem,
      translationPayload,
      agentList
    );
  }

  if (KANBAN_TYPES.includes(auditableType)) {
    translationPayload = handleKanban(auditLogItem, translationPayload);
  }

  if (auditableType === 'inboxmember' || auditableType === 'teammember') {
    translationPayload = handleInboxTeamMember(
      auditLogItem,
      translationPayload,
      agentList
    );
  }

  return translationPayload;
}

function getAccountUserUpdateSuffix(auditLogItem) {
  if (auditLogItem.auditable === null) {
    // If the user is deleted, we don't need to check if the user is the same as the auditLogItem.user_id
    // Else we can use the deleted translation key
    // It doesn't need the agent name
    return ':deleted';
  }
  return auditLogItem.user_id === auditLogItem.auditable.user_id
    ? ':self'
    : ':other';
}

export const generateLogActionKey = auditLogItem => {
  const auditableType = auditLogItem.auditable_type.toLowerCase();
  const action = auditLogItem.action.toLowerCase();
  let logActionKey = `${auditableType}:${action}`;

  if (auditableType === 'accountuser' && action === 'update') {
    logActionKey += getAccountUserUpdateSuffix(auditLogItem);
  }

  return translationKeys[logActionKey] || '';
};
